/**
 * Unit page — the components inside one vertical.
 *
 * Sibling of the course outline, and split down the middle on purpose:
 *
 *   THE LIST is ours.       `container_handler` and `container/…/children` are DRF, so the
 *                           JWT authenticates them and the rows, their errors and their
 *                           actions are drawn in our own chrome.
 *
 *   THE EDITOR is SHARED.  `ComponentEditor` — the same one the library page opens, over the
 *                           same server-side probe. Every XBlock type ships its own
 *                           `studio_view` (problem, video, and whatever a third-party block
 *                           installs), so the set of editors is open-ended and embedding the
 *                           platform's own means a block type installed tomorrow works here
 *                           with no change. The types Verawood has stripped that view from
 *                           fall back to our editor. None of that logic lives on this page.
 *
 * Freshness follows the outline's rule: after any write both queries are refetched rather
 * than patched locally. Adding a component changes the unit's publish state, deleting one
 * can change what the clipboard offers, and reproducing those rollups here would be a
 * second implementation of the platform's rules.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Icon,
  Spinner,
} from '../../shared/ui';
import {
  addComponent,
  blockIcon,
  getUnitChildren,
  getUnitContext,
  isContentContainer,
  pasteComponent,
  type ComponentTemplate,
  type UnitChild,
} from '../../shared/lib/unit';
import {
  deleteBlock,
  discardChanges,
  duplicateBlock,
  publishBlock,
  renameBlock,
  reorderChildren,
  updateBlockSettings,
  type BlockSettings,
  type OutlineBlock,
  type VisibilityState,
} from '../../shared/lib/studio';
import { ComponentEditor } from '../outline/ComponentEditor';
import { ConfigureDialog } from '../outline/ConfigureDialog';
import { LibraryPicker } from '../outline/LibraryPicker';
import { LinkedBlock } from '../outline/LinkedBlock';
import { PalettePicker } from '../outline/PalettePicker';
import { paletteGroups } from '../../shared/lib/palette';
import { UnitPreview } from '../outline/UnitPreview';
import '../outline/unit.css';

/* ------------------------------------------------------------------ route params ---- */

/**
 * Route params arrive percent-encoded because course and usage keys carry `:`, `+` and `@`.
 *
 * React Router already decodes once, so this is belt and braces for a key that was
 * double-encoded on the way in. It is safe to run twice because a key never contains a
 * literal `%`, and a malformed escape falls back to the raw value rather than blanking
 * the whole page.
 */
function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Turn Studio's sibling link into a route of ours.
 *
 * The server sends `quote_plus(reverse('container_handler', key))` — a fully escaped
 * `/container/<usage key>`, because it was written to be dropped into a query string in a
 * Mako template. Studio's own container page is not somewhere we want to send an author
 * (it needs Studio's chrome and its session), so only the key survives and it is routed
 * back to this page.
 */
function siblingUnitKey(url: string | null): string | null {
  if (!url) return null;
  const key = safeDecode(url).split('/').pop() ?? '';
  return key && key !== 'container' ? key : null;
}

/* ------------------------------------------------------- xblock_info → OutlineBlock ---- */

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const asBool = (value: unknown): boolean => value === true;

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const VISIBILITY_STATES: readonly string[] = [
  'live',
  'ready',
  'unscheduled',
  'needs_attention',
  'staff_only',
  'gated',
  'hide_from_toc',
];

/**
 * Adapt the unit's `xblock_info` to the shape the outline's dialog already speaks.
 *
 * `context.xblock_info` IS Studio's `create_xblock_info` payload — the same producer as
 * every node in the outline, so the field names already match `OutlineBlock` exactly. The
 * only reason this function exists is that the serializer declares the field as a bare
 * `DictField`, so TypeScript sees `Record<string, unknown>` and a blind `as OutlineBlock`
 * would be a lie: it would compile happily and then hand `ConfigureDialog` an `undefined`
 * where it expects `course_graders.map(...)`.
 *
 * So each field is read defensively with a default that is safe to submit. Nothing here
 * renames or reinterprets anything; if a field is missing the dialog simply shows the
 * conservative option rather than crashing on a payload from a release that dropped it.
 */
function unitAsOutlineBlock(
  unitKey: string,
  info: Record<string, unknown>,
  xblock: { display_name: string; category: string },
): OutlineBlock {
  const rawActions = info['actions'];
  const actions =
    typeof rawActions === 'object' && rawActions !== null
      ? (rawActions as Record<string, unknown>)
      : {};

  const visibility = info['visibility_state'];

  const rawUpstream = info['upstream_info'];
  const upstream =
    typeof rawUpstream === 'object' && rawUpstream !== null
      ? (rawUpstream as Record<string, unknown>)
      : null;

  return {
    id: asString(info['id'], unitKey),
    display_name: asString(info['display_name'], xblock.display_name),
    category: asString(info['category'], xblock.category),
    xblock_type: asString(info['xblock_type'], 'other'),
    has_children: asBool(info['has_children']),
    published: asBool(info['published']),
    has_changes: asBool(info['has_changes']),
    visibility_state:
      typeof visibility === 'string' && VISIBILITY_STATES.includes(visibility)
        ? (visibility as VisibilityState)
        : null,
    has_explicit_staff_lock: asBool(info['has_explicit_staff_lock']),
    ancestor_has_staff_lock: asBool(info['ancestor_has_staff_lock']),
    staff_only_message: asBool(info['staff_only_message']),
    hide_from_toc: asBool(info['hide_from_toc']),
    // Default false: the platform hides this control unless the feature is on, and
    // offering a toggle the server ignores is worse than not offering it.
    enable_hide_from_toc_ui: asBool(info['enable_hide_from_toc_ui']),
    start: asNullableString(info['start']),
    due: asNullableString(info['due']),
    release_date: asNullableString(info['release_date']),
    due_date: asString(info['due_date']),
    released_to_students: asBool(info['released_to_students']),
    graded: asBool(info['graded']),
    format: asNullableString(info['format']),
    course_graders: asStringArray(info['course_graders']),
    edited_on: asString(info['edited_on']),
    published_on: asNullableString(info['published_on']),
    lms_url: asString(info['lms_url']),
    actions: {
      // Permissions default to CLOSED. An absent flag means "the server did not say yes",
      // and the cost of a missing button is a reload — the cost of a wrongly offered one
      // is an action the author watches fail.
      deletable: asBool(actions['deletable']),
      draggable: asBool(actions['draggable']),
      childAddable: asBool(actions['childAddable']),
      duplicable: asBool(actions['duplicable']),
      unlinkable: typeof actions['unlinkable'] === 'boolean' ? actions['unlinkable'] : null,
    },
    upstream_info: upstream
      ? {
          upstream_ref: asNullableString(upstream['upstream_ref']),
          ready_to_sync: asBool(upstream['ready_to_sync']),
        }
      : null,
    explanatory_message: asNullableString(info['explanatory_message']),
  };
}

/**
 * Visibility state as something an author can act on.
 *
 * The same mapping the outline row uses. It is not exported from `OutlineNode`, and the
 * wording has to agree between the two pages — a unit that reads "Live" in the outline
 * and "Published" here is two different products. If one changes, change both.
 */
function unitStatus(block: OutlineBlock): {
  label: string;
  tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
} {
  switch (block.visibility_state) {
    case 'live':
      return { label: 'Live', tone: 'success' };
    case 'ready':
      return { label: 'Scheduled', tone: 'info' };
    case 'staff_only':
      return { label: 'Hidden', tone: 'warning' };
    case 'gated':
      return { label: 'Prerequisite', tone: 'info' };
    case 'hide_from_toc':
      return { label: 'Not in outline', tone: 'warning' };
    case 'needs_attention':
      return { label: 'Needs attention', tone: 'danger' };
    default:
      return { label: 'Unscheduled', tone: 'neutral' };
  }
}

/* --------------------------------------------------------------------- the page ---- */

type Confirm = { kind: 'delete'; child: UnitChild } | { kind: 'discard' };

/** Busy token for a palette entry, so one pick shows as working rather than all of them. */
const pickToken = (template: ComponentTemplate) =>
  `add:${template.category}:${template.boilerplate_name ?? ''}`;

export default function CourseUnit() {
  const params = useParams();
  const courseId = safeDecode(params['courseId'] ?? '');
  const unitId = safeDecode(params['unitId'] ?? '');
  const queryClient = useQueryClient();

  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [configuring, setConfiguring] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [editing, setEditing] = useState<UnitChild | null>(null);
  const [adding, setAdding] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  /**
   * Which parent the library picker is filling: the unit, or one Problem Bank inside it.
   *
   * A key rather than a boolean because the picker's whole job is `parent_locator`, and a
   * unit can hold two banks — "the picker is open" is not enough to know which one an author
   * meant.
   */
  const [linking, setLinking] = useState<{ key: string; name?: string } | null>(null);

  const context = useQuery({
    queryKey: ['unit-context', unitId],
    queryFn: () => getUnitContext(unitId),
    enabled: Boolean(unitId),
  });

  const children = useQuery({
    queryKey: ['unit-children', unitId],
    queryFn: () => getUnitChildren(unitId),
    enabled: Boolean(unitId),
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['unit-children', unitId] });
    void queryClient.invalidateQueries({ queryKey: ['unit-context', unitId] });
  }, [queryClient, unitId]);

  /**
   * One mutation for every write on this page.
   *
   * `locator` rides along purely so exactly one row — or one palette entry — shows as
   * busy, and the failure text is supplied per call because "could not delete" and "could
   * not reorder" need to reassure the author about different things.
   */
  const act = useMutation({
    mutationFn: (op: { locator: string; run: () => Promise<unknown>; failure: string }) => op.run(),
    onMutate: () => setFailure(null),
    onSuccess: (_data, op) => {
      invalidate();
      setConfirm(null);
      setConfiguring(false);
      // The palette closes HERE and not on the click, which is the whole of the fix for
      // "adding a component shows nothing for 4.7 seconds". Closing it synchronously in
      // `onPick` unmounted the only surface holding the busy state, so the screen was
      // byte-identical to before the click for as long as the write took — measured at
      // 4,723 ms — and the rational reading of that is that the click missed.
      //
      // Guarded on the locator rather than closing on every success, because this one
      // mutation serves every write on the page: a rename finishing while the palette
      // happened to be open must not close it.
      if (op.locator.startsWith('add:')) setAdding(false);
    },
    onError: (_error, op) => setFailure(op.failure),
  });

  const run = (locator: string, failure: string, fn: () => Promise<unknown>) =>
    act.mutate({ locator, failure, run: fn });

  const pending = act.isPending ? act.variables?.locator ?? null : null;

  const unitBlock = useMemo(
    () =>
      context.data
        ? unitAsOutlineBlock(unitId, context.data.xblock_info, context.data.xblock)
        : null,
    [context.data, unitId],
  );

  const kids = children.data?.children ?? [];

  // The SAME function the library page calls, differing only in which endpoint's answer it
  // is handed. `component_templates` already arrives grouped, so the course adapter keeps
  // Studio's own families and its own order; every rule about what the palette then offers
  // — the disclosure, the deprecated demotion, the search ranking — is in
  // shared/lib/palette.ts and is therefore the same on both surfaces.
  //
  // No capability filter here, deliberately, and it is not an omission: a course unit STORES
  // container blocks. `library_content` and `itembank` both hold children and are both
  // legitimately creatable in a unit, while a library refuses any of them with an HTTP 400.
  // That rejection is a property of the container, not of the shaping, so `paletteGroups`
  // keys it off the surface rather than trusting each caller to remember.
  const groups = useMemo(
    () => paletteGroups({ surface: 'course', groups: context.data?.component_templates ?? [] }),
    [context.data?.component_templates],
  );

  const prevKey = siblingUnitKey(context.data?.prev_url ?? null);
  const nextKey = siblingUnitKey(context.data?.next_url ?? null);
  const unitHref = (key: string) =>
    `/admin/courses/${encodeURIComponent(courseId)}/units/${encodeURIComponent(key)}`;
  const outlineHref = `/admin/courses/${encodeURIComponent(courseId)}`;

  const displayName = unitBlock?.display_name ?? children.data?.display_name ?? '';

  // A rename that lost the race with a refetch would otherwise leave the input showing a
  // name the server has already replaced.
  useEffect(() => setDraftName(displayName), [displayName]);

  function commitRename() {
    setRenaming(false);
    const next = draftName.trim();
    if (!next || next === displayName) {
      setDraftName(displayName);
      return;
    }
    run(unitId, 'The new name was not saved. The old one is still in place.', () =>
      renameBlock(unitId, next),
    );
  }

  function move(from: number, to: number) {
    if (to < 0 || to >= kids.length) return;
    // Studio's contract is the parent's full child list in its new order, so the move is
    // computed here as a splice rather than sent as a from/to pair.
    const order = kids.map((child) => child.block_id);
    const [moved] = order.splice(from, 1);
    if (moved === undefined) return;
    order.splice(to, 0, moved);

    run(moved, 'The order was not changed. The unit is still as it was.', () =>
      reorderChildren(unitId, order),
    );
  }

  function addFromTemplate(template: ComponentTemplate) {
    run(
      pickToken(template),
      `"${template.display_name}" was not added. Nothing in this unit changed.`,
      // BOTH halves travel together. `category` alone would create a blank problem where
      // the author asked for drag-and-drop: the boilerplate is what fills it in, and
      // dropping it produces a component that looks like a bug rather than an error.
      () => addComponent(unitId, template.category, template.boilerplate_name),
    );
  }

  if (!courseId || !unitId) {
    return (
      <Banner tone="danger">
        <strong>This address is incomplete.</strong> It names no course or no unit, so there is
        nothing to show. Open the course from Courses and pick a unit from its outline.
      </Banner>
    );
  }

  if (context.isLoading) return <Spinner label="Loading this unit…" />;

  if (context.isError || !context.data || !unitBlock) {
    return (
      <>
        <header className="workspace__page-head">
          <div>
            <h1>Unit</h1>
            <p className="unit-key">{unitId}</p>
          </div>
        </header>
        <Banner tone="danger">
          <strong>Could not load this unit.</strong> Either it no longer exists or you do not
          have authoring access to this course. Nothing has been changed —{' '}
          <Link to={outlineHref}>go back to the outline</Link> and open it from there.
        </Banner>
      </>
    );
  }

  const status = unitStatus(unitBlock);
  const ancestors = context.data.ancestor_xblocks;

  return (
    <>
      {/* Every ancestor links to the outline because the outline is where all of them live
          — we have one page for the whole tree, not a page per section. */}
      <nav className="unit-crumbs" aria-label="Breadcrumb">
        <Link to="/admin/courses">Courses</Link>
        {ancestors.map((ancestor, index) => (
          <span key={`${ancestor.title}-${index}`}>
            <Icon name="chevron_right" />
            <Link to={outlineHref}>{ancestor.title}</Link>
          </span>
        ))}
        <span>
          <Icon name="chevron_right" />
          <b aria-current="page">{displayName}</b>
        </span>
      </nav>

      <header className="workspace__page-head">
        <div>
          <div className="unit-title">
            {renaming ? (
              <RenameInput
                value={draftName}
                onChange={setDraftName}
                onCommit={commitRename}
                onCancel={() => {
                  setDraftName(displayName);
                  setRenaming(false);
                }}
              />
            ) : (
              <>
                <h1>{displayName}</h1>
                <button
                  type="button"
                  className="unit-rename"
                  onClick={() => setRenaming(true)}
                  aria-label={`Rename ${displayName}`}
                  title="Rename this unit"
                >
                  <Icon name="edit" />
                </button>
              </>
            )}
          </div>

          <div className="unit-badges">
            <Badge tone={status.tone}>{status.label}</Badge>
            {unitBlock.has_changes && unitBlock.published && (
              <Badge tone="warning">Draft changes</Badge>
            )}
            {!unitBlock.published && <Badge tone="neutral">Never published</Badge>}
            {unitBlock.upstream_info?.upstream_ref && (
              <Badge tone={unitBlock.upstream_info.ready_to_sync ? 'warning' : 'info'}>
                {unitBlock.upstream_info.ready_to_sync ? 'Library update available' : 'From library'}
              </Badge>
            )}
            <span className="unit-key">{unitId}</span>
          </div>

          {unitBlock.explanatory_message && (
            <p className="unit-explain">{unitBlock.explanatory_message}</p>
          )}
        </div>

        <div className="workspace__page-actions">
          {unitBlock.has_changes && (
            <Button
              variant="primary"
              busy={pending === unitId}
              onClick={() =>
                run(
                  unitId,
                  'Publishing failed. Nothing was published, so no learner saw a partial change.',
                  () => publishBlock(unitId),
                )
              }
            >
              <Icon name="publish" />
              Publish unit
            </Button>
          )}
          {unitBlock.has_changes && unitBlock.published && (
            <Button onClick={() => setConfirm({ kind: 'discard' })}>
              <Icon name="undo" />
              Discard changes
            </Button>
          )}
          <Button onClick={() => setPreviewing(true)}>
            <Icon name="visibility" />
            Preview
          </Button>
          <Button onClick={() => setConfiguring(true)}>
            <Icon name="tune" />
            Settings
          </Button>
          {unitBlock.lms_url && (
            <a className="ui-btn" data-variant="ghost" href={unitBlock.lms_url}>
              <Icon name="open_in_new" />
              View as learner
            </a>
          )}
        </div>
      </header>

      {failure && (
        <Banner tone="danger">
          <strong>{failure}</strong> If it keeps happening, reload the page to be sure of what
          is saved.
        </Banner>
      )}

      {(prevKey || nextKey) && (
        <nav className="unit-nav" aria-label="Nearby units">
          {prevKey ? (
            <Link className="unit-nav__link" to={unitHref(prevKey)}>
              <Icon name="arrow_back" />
              Previous unit
            </Link>
          ) : (
            <span />
          )}
          <span className="unit-nav__pos">Unit {context.data.position} in this subsection</span>
          {nextKey ? (
            <Link className="unit-nav__link" data-next to={unitHref(nextKey)}>
              Next unit
              <Icon name="arrow_forward" />
            </Link>
          ) : (
            <span />
          )}
        </nav>
      )}

      <Card title="Components">
        {children.isLoading ? (
          <Spinner label="Loading the components in this unit…" />
        ) : children.isError ? (
          <Banner tone="danger">
            <strong>Could not load the components in this unit.</strong> The unit itself loaded,
            so this was only the request for its contents. Reload the page; if it keeps failing,
            the unit may have been deleted while you were looking at it.
          </Banner>
        ) : kids.length === 0 ? (
          <EmptyState
            title="This unit is empty"
            body="A unit holds the things a learner actually reads, watches and answers. Add the first component to get started."
          />
        ) : (
          <ul className="unit-list">
            {kids.map((child, index) => (
              <ComponentRow
                key={child.block_id}
                child={child}
                index={index}
                siblingCount={kids.length}
                busy={pending === child.block_id}
                onEdit={() => setEditing(child)}
                onDuplicate={() =>
                  run(child.block_id, 'The copy was not created.', () =>
                    duplicateBlock(unitId, child.block_id),
                  )
                }
                onDelete={() => setConfirm({ kind: 'delete', child })}
                onMove={(to) => move(index, to)}
                onChooseContent={() =>
                  setLinking({ key: child.block_id, name: child.name })
                }
                onLinkChanged={() => {
                  void queryClient.invalidateQueries({ queryKey: ['unit-children', unitId] });
                  void queryClient.invalidateQueries({ queryKey: ['unit-context', unitId] });
                }}
              />
            ))}
          </ul>
        )}
      </Card>

      {/* Adding a component is an action, not furniture. It used to be an inline card
          listing thirty templates, which buried the unit's actual content under a wall of
          options; now it opens a searchable picker. */}
      <div className="unit-add">
        <Button
          variant="primary"
          // Busy for an add in flight only. `pending` is one token for the whole page, so
          // without the prefix test the button would also read "Working…" while an
          // unrelated row was being renamed or moved.
          busy={pending?.startsWith('add:') ?? false}
          onClick={() => setAdding(true)}
          disabled={groups.length === 0}
        >
          <Icon name="add" />
          Add a component
        </Button>
        {children.data?.can_paste_component && (
          <Button
            busy={pending === 'paste'}
            onClick={() =>
              run('paste', 'Nothing was pasted. This unit is unchanged.', () =>
                pasteComponent(unitId),
              )
            }
          >
            <Icon name="content_paste" />
            Paste
          </Button>
        )}
        {groups.length === 0 && (
          <Banner tone="warning">
            <strong>No component types are available here.</strong> The platform reports nothing
            this course may create, which usually means advanced module types have been
            restricted for it. An operator can change that in the course&rsquo;s advanced
            settings.
          </Banner>
        )}
      </div>

      {adding && (
        <PalettePicker
          groups={groups}
          pending={pending}
          // The same sentence the page shows, repeated inside the dialog because the
          // dialog is in the top layer and covers the page's own banner. Without this the
          // palette stays open on a failure and says nothing about why.
          error={
            failure ? (
              <Banner tone="danger">
                <strong>{failure}</strong> Nothing was added; you can try again or close
                this.
              </Banner>
            ) : undefined
          }
          onClose={() => setAdding(false)}
          // No `setAdding(false)` here. See the mutation's onSuccess: the palette stays
          // mounted so the picked tile can show as busy, and a failure lands on the
          // surface the author is looking at rather than behind it.
          onPick={addFromTemplate}
          onOpenLibrary={() => {
            setAdding(false);
            setLinking({ key: unitId });
          }}
        />
      )}

      {linking && (
        <LibraryPicker
          parentKey={linking.key}
          destinationName={linking.name}
          onClose={() => setLinking(null)}
          onLinked={() => {
            void queryClient.invalidateQueries({ queryKey: ['unit-children', unitId] });
            void queryClient.invalidateQueries({ queryKey: ['unit-context', unitId] });
            // The bank's own child list, so the row stops saying it is empty the moment it
            // is not. Keyed on the container, so this is a no-op when the unit was the
            // destination.
            void queryClient.invalidateQueries({ queryKey: ['unit-children', linking.key] });
          }}
        />
      )}

      {previewing && (
        <UnitPreview
          unitKey={unitId}
          unitName={unitBlock.display_name}
          hasChanges={unitBlock.has_changes}
          onClose={() => setPreviewing(false)}
        />
      )}

      {configuring && (
        <ConfigureDialog
          block={unitBlock}
          childLabel="Unit"
          busy={act.isPending}
          error={act.isError ? act.error : null}
          onClose={() => setConfiguring(false)}
          onSave={(settings: BlockSettings) =>
            run(unitId, 'The settings were not saved. This unit is unchanged.', () =>
              updateBlockSettings(unitId, settings),
            )
          }
        />
      )}

      {confirm?.kind === 'delete' && (
        <ConfirmDialog
          title="Delete this component?"
          confirmLabel="Delete component"
          busy={act.isPending}
          onClose={() => setConfirm(null)}
          onConfirm={() =>
            run(confirm.child.block_id, 'The component was not deleted.', () =>
              deleteBlock(confirm.child.block_id),
            )
          }
          body={
            <>
              <p>
                <strong>{confirm.child.name}</strong> will be removed from this unit. This cannot
                be undone.
              </p>
              {children.data?.is_published && (
                <Banner tone="warning">
                  This unit is published, so learners can see it now. Publish the unit again
                  afterwards to remove this component from the live course.
                </Banner>
              )}
            </>
          }
        />
      )}

      {confirm?.kind === 'discard' && (
        <ConfirmDialog
          title="Discard unpublished changes?"
          confirmLabel="Discard changes"
          busy={act.isPending}
          onClose={() => setConfirm(null)}
          onConfirm={() =>
            run(unitId, 'The changes were not discarded. This unit is unchanged.', () =>
              discardChanges(unitId),
            )
          }
          body={
            <p>
              <strong>{displayName}</strong> will go back to the version learners currently see.
              Every edit to this unit and its components since it was last published will be
              lost.
            </p>
          }
        />
      )}

      {editing && (
        <ComponentEditor
          key={editing.block_id}
          blockKey={editing.block_id}
          blockName={editing.name}
          publishTarget="unit"
          onClose={() => setEditing(null)}
          onSaved={invalidate}
        />
      )}
    </>
  );
}

/* ----------------------------------------------------------------------- pieces ---- */

/** Inline rename. Escape reverts, Enter and blur both commit — the outline's contract. */
function RenameInput({
  value,
  onChange,
  onCommit,
  onCancel,
}: {
  value: string;
  onChange: (v: string) => void;
  onCommit: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => ref.current?.select(), []);

  return (
    <input
      ref={ref}
      className="unit-input"
      value={value}
      aria-label="Unit name"
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onCommit();
        if (e.key === 'Escape') onCancel();
      }}
    />
  );
}

/**
 * One component in the unit.
 *
 * Which buttons appear comes from `child.actions`, computed server-side. A block whose
 * parent is a library-linked container, or one living outside a course, forbids a
 * different subset — reasons the browser cannot see. Deriving the buttons from
 * `block_type` instead would offer actions the server then refuses.
 */
function ComponentRow({
  child,
  index,
  siblingCount,
  busy,
  onEdit,
  onDuplicate,
  onDelete,
  onMove,
  onLinkChanged,
  onChooseContent,
}: {
  child: UnitChild;
  index: number;
  siblingCount: number;
  busy: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onMove: (to: number) => void;
  onLinkChanged: () => void;
  /** Open the library picker with THIS block as the parent. Container rows only. */
  onChooseContent: () => void;
}) {
  const broken = child.render_error.trim().length > 0;
  const container = isContentContainer(child.block_type);

  /**
   * What is inside a container, asked only of containers.
   *
   * It is the one thing the unit's own child list cannot tell us and the one thing that
   * decides what a learner sees: a container renders its children and nothing else, so an
   * empty one is a block that occupies a row here and zero characters there. The platform
   * does not warn about it either — `ItemBankMixin.validate()` complains about a bad Count
   * and about selecting FEWER than Count, and says nothing at all about none.
   */
  const contents = useQuery({
    queryKey: ['unit-children', child.block_id],
    queryFn: () => getUnitChildren(child.block_id),
    enabled: container,
    retry: false,
  });
  const contentCount = contents.data?.children.length ?? null;
  const legacyLibraryBlock = child.block_type === 'library_content';

  return (
    <li className="unit-item" data-busy={busy || undefined} data-broken={broken || undefined}>
      <div className="unit-item__row">
        {/* `data-type` drives the tint, using the same families as the palette so a
            component looks the same colour wherever the author meets it — and `blockIcon`
            rather than `componentIcon` for the same reason one line down. A Poll picked from
            a palette showing `ballot` must not land in this list as a generic `widgets`. */}
        <span className="unit-item__icon" data-type={child.block_type} aria-hidden="true">
          <Icon name={blockIcon(child.block_type)} />
        </span>

        <div className="unit-item__title">
          {/* The name is the control. Clicking a component to edit it is what an author
              expects, and a button rather than a div keeps it on the tab order. */}
          <button type="button" className="unit-item__name" onClick={onEdit}>
            {child.name}
          </button>
          <div className="unit-item__meta">
            <span className="unit-item__type">{child.block_type}</span>
            {child.upstream_link?.upstream_ref && !child.upstream_link.ready_to_sync && (
              <Badge tone="info">From library</Badge>
            )}
          </div>
        </div>

        <div className="unit-item__actions">
          {child.actions.can_move && siblingCount > 1 && (
            // The keyboard path to reordering. Not optional: this is a core action and
            // drag and drop is unusable without a pointer.
            <>
              <IconButton
                icon="arrow_upward"
                label={`Move ${child.name} up`}
                disabled={index === 0}
                onClick={() => onMove(index - 1)}
              />
              <IconButton
                icon="arrow_downward"
                label={`Move ${child.name} down`}
                disabled={index === siblingCount - 1}
                onClick={() => onMove(index + 1)}
              />
            </>
          )}
          {/* A container's content is not in its editor. The platform's `studio_view` for
              both of these is `widgets/metadata-edit.html` — Count and Show Reset Button —
              so an author who opens Edit looking for "what is in this bank" finds two
              settings and leaves. Studio puts the picker in the block's `author_view`,
              behind a page this app does not serve. This is that button. */}
          {container && !legacyLibraryBlock && (
            <Button onClick={onChooseContent}>
              <Icon name="library_books" />
              Choose content
            </Button>
          )}
          <IconButton icon="edit" label={`Edit ${child.name}`} onClick={onEdit} />
          {child.actions.can_duplicate && (
            <IconButton
              icon="content_copy"
              label={`Duplicate ${child.name}`}
              onClick={onDuplicate}
            />
          )}
          {child.actions.can_delete && (
            <IconButton icon="delete" label={`Delete ${child.name}`} danger onClick={onDelete} />
          )}
        </div>
      </div>

      {/* A block that failed to render shows why, in place of the blank row it would
          otherwise be. The row's actions stay available — deleting a broken block is
          usually exactly what the author came to do. */}
      {broken && (
        <Banner tone="danger">
          <strong>This component could not be rendered.</strong> {child.render_error} Editing it
          may still work; if not, delete it and add a replacement.
        </Banner>
      )}

      {/* AN EMPTY CONTAINER IS A BLANK PAGE, and only this row can say so.
          A learner's frame renders `data-block-type="itembank"` with an innerText length of
          zero — no heading, no placeholder, nothing to click and nothing to report. Measured
          on dev 2026-08-29 on a bank an author had created, named and saved. */}
      {container && contentCount === 0 && !legacyLibraryBlock && (
        <Banner tone="warning">
          <strong>This problem bank is empty, so learners see nothing here.</strong> A bank
          shows a selection of components drawn from a content library; until you choose some,
          it renders as a blank space in the unit. Use <em>Choose content</em> to add them.
        </Banner>
      )}

      {/* `library_content` is a different case and deserves different words: it is not empty
          pending a choice, it is unfillable. Its source has to be a LEGACY (v1) library —
          `xmodule/library_content_block.py` resolves `source_library_id` through
          `LibraryLocator`, which only parses a `library-v1:` key — and this platform has none
          and offers no way to make one. Every content library here is v2. So the block can be
          created, named, saved and published, and can never show a learner anything. */}
      {legacyLibraryBlock && (
        <Banner tone="warning">
          <strong>This block cannot show learners anything on this platform.</strong> A
          Randomized Content Block draws from a legacy content library, and this platform only
          has the current kind. It is deprecated upstream for exactly that reason. Delete it
          and add a <em>Problem Bank</em> instead, which does the same job from the libraries
          you actually have.
        </Banner>
      )}

      {/* The library link, and the decision it may be waiting on. Below the row rather than
          inside it: an update prompt carries buttons, and buttons inside the title line would
          compete with the component's own actions. */}
      {child.upstream_link?.upstream_ref && (
        <LinkedBlock
          blockKey={child.block_id}
          link={child.upstream_link}
          onChanged={onLinkChanged}
        />
      )}

      {/* Validation messages are real content errors — a problem with no correct answer,
          a video with no source. They belong on the row, not behind a tooltip. */}
      {child.validation_messages.map((message, messageIndex) => (
        <Banner
          key={`${message.type}-${messageIndex}`}
          tone={message.type === 'error' ? 'danger' : 'warning'}
        >
          {message.text}
        </Banner>
      ))}
    </li>
  );
}

function IconButton({
  icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      className="unit-icon-btn"
      data-danger={danger || undefined}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      <Icon name={icon} />
    </button>
  );
}
